import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import type { UnidadMedida } from '../types/ingredientes';

// Helper para capitalizar marca
const capitalizarMarca = (marca: string): string => {
  if (!marca || marca.trim() === '') return 'Genérica';
  const trimmed = marca.trim().toLowerCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

interface IngredienteFormSimpleProps {
  onSave: (ingredienteData: {
    nombre: string;
    marca?: string;
    descripcion: string;
    categoria: string;
    unidadMedidaId: string;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
  nombreInicial?: string;
}

const IngredienteFormSimple: React.FC<IngredienteFormSimpleProps> = ({
  onSave,
  onCancel,
  loading = false,
  nombreInicial = '',
}) => {
  const [formData, setFormData] = useState({
    nombre: nombreInicial,
    marca: 'genérica',
    descripcion: '',
    categoria: '',
    unidadMedidaId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Actualizar el nombre cuando cambie la prop nombreInicial
  useEffect(() => {
    if (nombreInicial) {
      setFormData((prev) => ({
        ...prev,
        nombre: nombreInicial,
      }));
    }
  }, [nombreInicial]);

  // Unidades de medida básicas - IDs que coinciden con el mapeo del service
  const unidadesMedida: UnidadMedida[] = [
    {
      id: '1',
      nombre: 'Kilogramos',
      abreviacion: 'kg',
      tipo: 'peso',
      activa: true,
    },
    {
      id: '2',
      nombre: 'Litros',
      abreviacion: 'l',
      tipo: 'volumen',
      activa: true,
    },
    {
      id: '3',
      nombre: 'Unidades',
      abreviacion: 'u',
      tipo: 'unidad',
      activa: true,
    },
    {
      id: '4',
      nombre: 'Gramos',
      abreviacion: 'g',
      tipo: 'peso',
      activa: true,
    },
    {
      id: '5',
      nombre: 'Mililitros',
      abreviacion: 'ml',
      tipo: 'volumen',
      activa: true,
    },
  ];

  const categoriasIngredientes = [
    'Carnes',
    'Verduras',
    'Frutas',
    'Lácteos',
    'Granos',
    'Condimentos',
    'Aceites',
    'Especias',
    'Hierbas',
    'Otros',
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.categoria) {
      newErrors.categoria = 'La categoría es requerida';
    }

    if (!formData.unidadMedidaId) {
      newErrors.unidadMedidaId = 'La unidad de medida es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-400">
          Crear Nuevo Ingrediente
        </h4>
        <button
          onClick={onCancel}
          className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Nombre del ingrediente */}
        <div>
          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
            Nombre del ingrediente *
          </label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleInputChange('nombre', e.target.value)}
            placeholder="Ej: Tomate Cherry"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${
              errors.nombre
                ? 'border-red-300 dark:border-red-600'
                : 'border-blue-300 dark:border-blue-600'
            }`}
          />
          {errors.nombre && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.nombre}
            </p>
          )}
        </div>

        {/* Marca */}
        <div>
          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
            Marca
          </label>
          <input
            type="text"
            value={formData.marca}
            onChange={(e) =>
              handleInputChange('marca', capitalizarMarca(e.target.value))
            }
            placeholder="Ej: Chef, Gallo (o genérica)"
            className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>

        {/* Categoría */}
        <div>
          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
            Categoría *
          </label>
          <select
            value={formData.categoria}
            onChange={(e) => handleInputChange('categoria', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${
              errors.categoria
                ? 'border-red-300 dark:border-red-600'
                : 'border-blue-300 dark:border-blue-600'
            }`}
          >
            <option value="">Seleccionar categoría</option>
            {categoriasIngredientes.map((categoria) => (
              <option key={categoria} value={categoria}>
                {categoria}
              </option>
            ))}
          </select>
          {errors.categoria && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.categoria}
            </p>
          )}
        </div>

        {/* Unidad de medida */}
        <div>
          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
            Unidad de medida *
          </label>
          <select
            value={formData.unidadMedidaId}
            onChange={(e) =>
              handleInputChange('unidadMedidaId', e.target.value)
            }
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${
              errors.unidadMedidaId
                ? 'border-red-300 dark:border-red-600'
                : 'border-blue-300 dark:border-blue-600'
            }`}
          >
            <option value="">Seleccionar unidad</option>
            {unidadesMedida.map((unidad) => (
              <option key={unidad.id} value={unidad.id}>
                {unidad.nombre} ({unidad.abreviacion})
              </option>
            ))}
          </select>
          {errors.unidadMedidaId && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.unidadMedidaId}
            </p>
          )}
        </div>

        {/* Descripción (opcional) */}
        <div>
          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
            Descripción
          </label>
          <textarea
            value={formData.descripcion}
            onChange={(e) => handleInputChange('descripcion', e.target.value)}
            placeholder="Descripción opcional del ingrediente..."
            rows={2}
            className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>

        {/* Botones */}
        <div className="flex space-x-2 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Crear</span>
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default IngredienteFormSimple;
